/**
 * 五行／生剋查表（可催運盤）
 *
 * ## 日干落宮生我／我剋（洛書宮位五行）
 * 以「日干落宮」之洛書宮位五行為「我」，與各宮洛書五行比較。
 *   生我 = 該宮洛書五行 生 日干落宮五行
 *   我剋 = 日干落宮五行 剋 該宮洛書五行
 * 例：日干己落離9（火）→ 生我＝木宮震3巽4；我剋＝金宮乾6兌7。
 *
 * ## 跨層／跨符號生剋（亦用洛書宮位五行・非符號固有五行）
 * 使用者選 Item A（天干／門／星／神之一）、關係「生」或「剋」、Item B。
 * **只用** A／B 所在宮的洛書五行（PALACE_WUXING），與日干落宮生我／我剋同一套表。
 * **不用** 天干／門／星／神固有五行（勿把「乙剋芮」解成乙木剋芮土）。
 * 放置／命中規則：
 *   1) 在八宮找出含 A 的宮、含 B 的宮（排除中宮洛書5；星層中五固定禽→芮不參與）
 *   2) 存在一對 (Pa, Pb)：Pa 洛書五行 生/剋 Pb 洛書五行（標準五行相生剋）
 *   3) 「須同宮」預設關＝Pa、Pb 可異宮；開＝須 Pa===Pb（同宮五行相同→生/剋通常為0）
 * 例：「乙剋芮」＝乙所在宮洛書 剋 飛布芮所在宮洛書；
 *     「合生乙」＝合所在宮洛書 生 乙所在宮洛書。
 *
 * 層（天干／門／星／神）細項（日干落宮模式用）：
 *   - 無指定細項 → 該宮有該層內容即通過（存在過濾）
 *   - 有指定細項（如僅生門）→ 該宮該層值須命中其一
 *
 * 洛書宮位五行：坎1水 坤2土 震3木 巽4木 中5土 乾6金 兌7金 艮8土 離9火
 * 可催運生我／我剋／跨層宮集合不含中5（見 cuiyun.isCuiyunRelationPalace）。
 */

export type Wuxing = '木' | '火' | '土' | '金' | '水';

export type CuiyunLayer = 'gan' | 'men' | 'xing' | 'shen';

export type ShengKeKind = '生我' | '我剋' | '同我' | '我生' | '剋我' | '';

const SHENG: Record<Wuxing, Wuxing> = {
  木: '火',
  火: '土',
  土: '金',
  金: '水',
  水: '木',
};

const KE: Record<Wuxing, Wuxing> = {
  木: '土',
  火: '金',
  土: '水',
  金: '木',
  水: '火',
};

/** 洛書宮號 → 宮位五行 */
export const PALACE_WUXING: Record<number, Wuxing> = {
  1: '水', // 坎
  2: '土', // 坤
  3: '木', // 震
  4: '木', // 巽
  5: '土', // 中
  6: '金', // 乾
  7: '金', // 兌
  8: '土', // 艮
  9: '火', // 離
};

/** 天干 → 五行 */
export const STEM_WUXING: Record<string, Wuxing> = {
  甲: '木',
  乙: '木',
  丙: '火',
  丁: '火',
  戊: '土',
  己: '土',
  庚: '金',
  辛: '金',
  壬: '水',
  癸: '水',
};

/**
 * 八門 → 五行（含引擎簡體傷／开／惊）
 * 休水、生土、伤木、杜木、景火、死土、惊金、开金
 */
export const DOOR_WUXING: Record<string, Wuxing> = {
  休: '水',
  生: '土',
  伤: '木',
  傷: '木',
  杜: '木',
  景: '火',
  死: '土',
  惊: '金',
  驚: '金',
  开: '金',
  開: '金',
};

/**
 * 九星 → 五行（禽＝土；UI 顯示芮，同土）
 * 蓬水、芮／禽土、冲木、辅木、心金、柱金、任土、英火
 */
export const STAR_WUXING: Record<string, Wuxing> = {
  蓬: '水',
  芮: '土',
  禽: '土',
  冲: '木',
  沖: '木',
  辅: '木',
  輔: '木',
  心: '金',
  柱: '金',
  任: '土',
  英: '火',
};

/**
 * 八神（引擎 abbreviated）→ 五行
 * 假設（見 STATUS）：
 *   符(值符)土、蛇(螣蛇)火、阴(太陰)金、合(六合)木、
 *   虎(白虎)金、武(玄武)水、九(九地)土、天(九天)金
 * 阴遁盤面仍用虎／武簡稱（非勾陳／朱雀），故不另分陰陽映射。
 */
export const SHEN_WUXING: Record<string, Wuxing> = {
  符: '土',
  蛇: '火',
  阴: '金',
  陰: '金',
  合: '木',
  虎: '金',
  武: '水',
  九: '土',
  地: '土',
  天: '金',
};

export function palaceWuxing(luoshu: number): Wuxing | null {
  return PALACE_WUXING[luoshu] ?? null;
}

export function stemWuxing(stem: string): Wuxing | null {
  return STEM_WUXING[stem] ?? null;
}

export function doorWuxing(door: string): Wuxing | null {
  if (!door || door === '-' || door === '·') return null;
  return DOOR_WUXING[door] ?? null;
}

export function starWuxing(star: string): Wuxing | null {
  if (!star || star === '-' || star === '·') return null;
  return STAR_WUXING[star] ?? null;
}

export function shenWuxing(shen: string): Wuxing | null {
  if (!shen || shen === '-' || shen === '·') return null;
  return SHEN_WUXING[shen] ?? null;
}

/** a 對 b：生我／我剋／同我／我生／剋我 */
export function relationOf(a: Wuxing, b: Wuxing): ShengKeKind {
  if (a === b) return '同我';
  if (SHENG[a] === b) return '我生';
  if (SHENG[b] === a) return '生我';
  if (KE[a] === b) return '我剋';
  if (KE[b] === a) return '剋我';
  return '';
}

/** 宮位五行相對「我」：是否為「生我」（宮五行 生 我） */
export function isShengWo(palaceWx: Wuxing, meWx: Wuxing): boolean {
  return SHENG[palaceWx] === meWx;
}

/** 宮位五行相對「我」：是否為「我剋」（我 剋 宮五行） */
export function isWoKe(palaceWx: Wuxing, meWx: Wuxing): boolean {
  return KE[meWx] === palaceWx;
}

export function layerWuxing(
  layer: CuiyunLayer,
  gan: string,
  door: string,
  star: string,
  shen: string,
): Wuxing | null {
  switch (layer) {
    case 'gan':
      return stemWuxing(gan);
    case 'men':
      return doorWuxing(door);
    case 'xing':
      return starWuxing(star);
    case 'shen':
      return shenWuxing(shen);
  }
}

/** 該宮是否有該層內容（存在過濾，不比對五行） */
export function layerHasPresence(
  layer: CuiyunLayer,
  gan: string,
  door: string,
  star: string,
  shen: string,
): boolean {
  const empty = (s: string) => !s || s === '-' || s === '·';
  switch (layer) {
    case 'gan':
      return !empty(gan);
    case 'men':
      return !empty(door);
    case 'xing':
      return !empty(star);
    case 'shen':
      return !empty(shen);
  }
}

/** 繁中層名 */
export const LAYER_LABEL: Record<CuiyunLayer, string> = {
  gan: '天干',
  men: '門',
  xing: '星',
  shen: '神',
};

/** 可選細項（canonical＝引擎實際輸出；label＝繁中 UI） */
export interface LayerItemOption {
  /** 正規化比對鍵（與引擎值對齊） */
  id: string;
  /** 繁中顯示 */
  label: string;
}

/** 天盤干（六儀三奇；甲不飛布） */
export const GAN_ITEMS: LayerItemOption[] = [
  { id: '戊', label: '戊' },
  { id: '己', label: '己' },
  { id: '庚', label: '庚' },
  { id: '辛', label: '辛' },
  { id: '壬', label: '壬' },
  { id: '癸', label: '癸' },
  { id: '丁', label: '丁' },
  { id: '丙', label: '丙' },
  { id: '乙', label: '乙' },
];

/** 八門（引擎簡體伤／开／惊；UI 繁中） */
export const MEN_ITEMS: LayerItemOption[] = [
  { id: '休', label: '休' },
  { id: '生', label: '生' },
  { id: '伤', label: '傷' },
  { id: '杜', label: '杜' },
  { id: '景', label: '景' },
  { id: '死', label: '死' },
  { id: '惊', label: '驚' },
  { id: '开', label: '開' },
];

/** 九星（禽→芮；引擎冲／辅） */
export const XING_ITEMS: LayerItemOption[] = [
  { id: '蓬', label: '蓬' },
  { id: '芮', label: '芮' },
  { id: '冲', label: '沖' },
  { id: '辅', label: '輔' },
  { id: '心', label: '心' },
  { id: '柱', label: '柱' },
  { id: '任', label: '任' },
  { id: '英', label: '英' },
];

/** 八神（引擎阴） */
export const SHEN_ITEMS: LayerItemOption[] = [
  { id: '符', label: '符' },
  { id: '蛇', label: '蛇' },
  { id: '阴', label: '陰' },
  { id: '合', label: '合' },
  { id: '虎', label: '虎' },
  { id: '武', label: '武' },
  { id: '九', label: '九' },
  { id: '天', label: '天' },
];

export const LAYER_ITEMS: Record<CuiyunLayer, LayerItemOption[]> = {
  gan: GAN_ITEMS,
  men: MEN_ITEMS,
  xing: XING_ITEMS,
  shen: SHEN_ITEMS,
};

/** 繁簡／禽芮等同形正規化為 picker id */
const NORM_MAP: Record<string, string> = {
  傷: '伤',
  開: '开',
  驚: '惊',
  沖: '冲',
  輔: '辅',
  陰: '阴',
  禽: '芮',
};

export function normalizeLayerItem(raw: string): string {
  if (!raw || raw === '-' || raw === '·') return '';
  return NORM_MAP[raw] ?? raw;
}

/** 顯示用繁中標籤（找不到則回傳原字） */
export function layerItemLabel(layer: CuiyunLayer, raw: string): string {
  const id = normalizeLayerItem(raw);
  if (!id) return '';
  const opt = LAYER_ITEMS[layer].find((o) => o.id === id);
  return opt?.label ?? raw;
}

/**
 * 層是否命中：無細項＝存在過濾；有細項＝值須在選集（正規化後）。
 * selected 空陣列／undefined → 僅存在過濾。
 */
export function layerMatches(
  layer: CuiyunLayer,
  gan: string,
  door: string,
  star: string,
  shen: string,
  selected?: string[] | null,
): boolean {
  if (!layerHasPresence(layer, gan, door, star, shen)) return false;
  if (!selected || selected.length === 0) return true;

  const want = new Set(selected.map(normalizeLayerItem).filter(Boolean));
  if (!want.size) return true;

  let raw = '';
  switch (layer) {
    case 'gan':
      raw = gan;
      break;
    case 'men':
      raw = door;
      break;
    case 'xing':
      raw = star;
      break;
    case 'shen':
      raw = shen;
      break;
  }
  const id = normalizeLayerItem(raw);
  return Boolean(id && want.has(id));
}

/** 取出該宮該層之正規化 id（無則 ''） */
export function layerItemId(
  layer: CuiyunLayer,
  gan: string,
  door: string,
  star: string,
  shen: string,
): string {
  let raw = '';
  switch (layer) {
    case 'gan':
      raw = gan;
      break;
    case 'men':
      raw = door;
      break;
    case 'xing':
      raw = star;
      break;
    case 'shen':
      raw = shen;
      break;
  }
  return normalizeLayerItem(raw);
}

/** a 的五行是否生 b 的五行（標準五行相生，無特例） */
export function wxSheng(a: Wuxing, b: Wuxing): boolean {
  return SHENG[a] === b;
}

/** a 的五行是否剋 b 的五行（標準五行相剋，無特例） */
export function wxKe(a: Wuxing, b: Wuxing): boolean {
  return KE[a] === b;
}

/**
 * 符號固有五行（天干／門／星／神表內）。僅供顯示／參考。
 * 跨層生剋過濾**不用**此表，改用所在宮洛書五行（palaceWuxing）。
 */
export function itemWuxing(layer: CuiyunLayer, rawId: string): Wuxing | null {
  const id = normalizeLayerItem(rawId);
  if (!id) return null;
  switch (layer) {
    case 'gan':
      return STEM_WUXING[id] ?? null;
    case 'men':
      return DOOR_WUXING[id] ?? null;
    case 'xing':
      return STAR_WUXING[id] ?? null;
    case 'shen':
      return SHEN_WUXING[id] ?? null;
  }
}

/**
 * 兩洛書宮位五行是否滿足「Pa 生 Pb」或「Pa 剋 Pb」。
 * 跨層生剋與日干落宮生我／我剋共用此標準表。
 */
export function palacesElementRelate(
  aLuoshu: number,
  relation: '生' | '剋',
  bLuoshu: number,
): boolean {
  const aw = PALACE_WUXING[aLuoshu];
  const bw = PALACE_WUXING[bLuoshu];
  if (!aw || !bw) return false;
  return relation === '生' ? wxSheng(aw, bw) : wxKe(aw, bw);
}
